// Η λογική της οθόνης «Κρατήσεις» του ταμείου: καθαρές συναρτήσεις (τεστάρονται).
// Ίδιοι κανόνες με τη Sala: «αργεί» μετά από 15′, πιασμένα τραπέζια = ανοιχτοί λογαριασμοί
// και παρέες που κάθονται, η επόμενη κίνηση εξαρτάται από την κατάσταση.

import type { Day, DayTable, Reservation, ResAction, ResStatus } from './types';

export const LATE_MIN = 15;
export type Filter = 'active' | 'seated' | 'all';

export const toMin = (hhmm: string) => {
  const [h, m] = hhmm.split(':').map(Number);
  return h * 60 + (m || 0);
};
export const fmt = (min: number) => `${String(Math.floor(min / 60) % 24).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;

/** Σημερινή ημερομηνία Ελλάδας (YYYY-MM-DD), όπου κι αν τρέχει το ταμείο. */
export function athensToday(now: Date | number = Date.now()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Athens', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(now));
}
export function addDays(date: string, n: number) {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

export const tablesOf = (r: Pick<Reservation, 'table_id' | 'extra_table_ids'>) =>
  [r.table_id, ...(r.extra_table_ids || [])].filter((x): x is number => x != null).map(Number);

const ACTIVE: ResStatus[] = ['pending', 'confirmed', 'arrived', 'waitlist'];
const GONE: ResStatus[] = ['cancelled', 'no_show'];

/** Αργεί: ήταν να έρθει (σήμερα) και πέρασαν 15′ από την ώρα του. */
export function isLate(r: Reservation, today: string, nowMin: number) {
  return (r.status === 'pending' || r.status === 'confirmed') && r.reservation_date === today && nowMin - toMin(r.reservation_time) >= LATE_MIN;
}

export function matches(r: Reservation, filter: Filter, q: string) {
  if (filter === 'active' && !ACTIVE.includes(r.status)) return false;
  if (filter === 'seated' && r.status !== 'seated') return false;
  const s = q.trim().toLocaleLowerCase('el');
  if (!s) return true;
  const digits = s.replace(/\D/g, '');
  return r.customer_name.toLocaleLowerCase('el').includes(s)
    || (!!digits && digits.length >= 3 && (r.customer_phone || '').replace(/\D/g, '').includes(digits))
    || (r.code || '').toLowerCase() === s;
}

/** Ομάδες ανά ώρα (12:30, 13:00…), με τη σειρά της μέρας. */
export function byTime(list: Reservation[]) {
  const map = new Map<string, Reservation[]>();
  for (const r of [...list].sort((a, b) => toMin(a.reservation_time) - toMin(b.reservation_time) || a.customer_name.localeCompare(b.customer_name, 'el'))) {
    const k = r.reservation_time.slice(0, 5);
    map.set(k, [...(map.get(k) || []), r]);
  }
  return Array.from(map, ([time, items]) => ({ time, items }));
}

export function counts(list: Reservation[]) {
  const live = list.filter(r => !GONE.includes(r.status));
  return {
    bookings: live.length,
    covers: live.reduce((s, r) => s + r.party_size, 0),
    coming: list.filter(r => r.status === 'pending' || r.status === 'confirmed').length,
    arrived: list.filter(r => r.status === 'arrived').length,
    seated: list.filter(r => r.status === 'seated').length,
    pending: list.filter(r => r.status === 'pending').length,
  };
}

/** Τραπέζια πιασμένα ΤΩΡΑ: ανοιχτοί λογαριασμοί (walk-in) και παρέες που κάθονται. */
export function occupied(day: Pick<Day, 'walk_ins' | 'reservations'>) {
  const s = new Set<number>(day.walk_ins.map(w => Number(w.table_id)));
  for (const r of day.reservations) if (r.status === 'seated') tablesOf(r).forEach(id => s.add(id));
  return s;
}

/** Για το «Κάθισε»: πρώτα ελεύθερα που χωρούν την παρέα (το μικρότερο πρώτο), μετά τα υπόλοιπα. */
export function seatOrder(tables: DayTable[], party: number, busy: Set<number>, preferred: number[] = []) {
  const rank = (t: DayTable) => (busy.has(t.id) ? 3 : preferred.includes(t.id) ? 0 : t.capacity >= party ? 1 : 2);
  return [...tables].sort((a, b) => rank(a) - rank(b) || a.capacity - b.capacity
    || a.table_number.localeCompare(b.table_number, 'el', { numeric: true }));
}

/** Η κύρια κίνηση για μια κράτηση (το μεγάλο κουμπί της γραμμής). */
export function primaryAction(r: Reservation): ResAction | null {
  if (r.status === 'pending') return 'confirm';
  if (r.status === 'confirmed' || r.status === 'arrived' || r.status === 'waitlist') return 'seat';
  return null;
}

/** Οι υπόλοιπες κινήσεις που επιτρέπει ο server από την τρέχουσα κατάσταση. */
export function otherActions(r: Reservation, today: string): ResAction[] {
  const out: ResAction[] = [];
  if (r.status === 'pending' || r.status === 'confirmed') out.push('arrive');
  if (r.status === 'seated' || r.status === 'arrived') out.push('complete');
  if (ACTIVE.includes(r.status)) out.push('cancel');
  if ((r.status === 'pending' || r.status === 'confirmed') && r.reservation_date <= today) out.push('no-show');
  return out;
}
